import { useCallback, useRef, useState } from 'react'
import AsyncSelect, { StylesConfig } from 'react-select/async'

interface BranchOption {
	[key: string]: string,
	label: string
	value: string
}

interface AsyncOptionSelectProps<T> {
	clearable?: boolean,
	disabled?: boolean,
	item: T,
	loadOptions: (item: T, inputValue: string) => Promise<BranchOption[]>,
	onChange: (value: string | null) => void,
	optionLabel?: string,
	optionValue?: string,
	value: string | null | undefined
}

const asyncSelectStyles: StylesConfig<BranchOption, false> = {
	control: (base) => ({
		...base,
		minHeight: '32px',
		border: 'none',
		boxShadow: 'none',
		backgroundColor: 'transparent',
	}),
	valueContainer: (base) => ({
		...base,
		padding: '0 8px',
	}),
	indicatorSeparator: () => ({
		display: 'none',
	}),
	menu: (base) => ({
		...base,
		zIndex: 9999,
	}),
	menuPortal: (base) => ({
		...base,
		zIndex: 9999,
	}),
}

export const AsyncOptionSelect = <T,>({
	item,
	value,
	onChange,
	disabled,
	loadOptions,
	optionValue = 'value',
	optionLabel = 'label',
	clearable,
}: AsyncOptionSelectProps<T>) => {
	const [options, setOptions] = useState<BranchOption[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const hasFetched = useRef(false)

	const loadOptionsHandler = useCallback(
		(inputValue: string) => loadOptions(item, inputValue),
		[item, loadOptions],
	)

	const handleMenuOpen = useCallback(async () => {
		if (hasFetched.current || isLoading) return
		setIsLoading(true)
		try {
			const result = await loadOptions(item, '')
			setOptions(result)
			hasFetched.current = true
		} finally {
			setIsLoading(false)
		}
	}, [item, loadOptions, isLoading])

	const currentValue: BranchOption | null = value
		? { [optionValue]: value, [optionLabel]: value, value, label: value }
		: null

	const handleChange = useCallback(
		(option: BranchOption | null) => {
			onChange(option?.[optionValue] ?? null)
		},
		[onChange, optionValue],
	)

	return (
		<AsyncSelect<BranchOption>
			cacheOptions
			defaultOptions={options}
			loadOptions={loadOptionsHandler}
			value={currentValue}
			onChange={handleChange}
			isDisabled={disabled}
			isClearable={clearable}
			isLoading={isLoading}
			onMenuOpen={handleMenuOpen}
			placeholder=""
			styles={asyncSelectStyles}
			getOptionValue={(opt) => opt[optionValue]}
			getOptionLabel={(opt) => opt[optionLabel]}
			menuPortalTarget={document.body}
			menuPlacement="auto"
		/>
	)
}

